import { useEffect, useMemo, useRef, useState } from 'react'
import { formatTimecode, framesToSeconds } from '@easyedit/timeline'
import type { MediaAsset } from '../../lib/api/types'
import { PlaybackEngine } from '../../lib/playback/engine'
import type { MediaSource } from '../../lib/playback/mediaPool'
import { useDuration, useEditorStore } from '../../stores/editorStore'
import { isTextEntry } from '../../lib/dom/focus'

export function Preview({ assets }: { assets: Map<string, MediaAsset> }) {
  const containerRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const engineRef = useRef<PlaybackEngine | null>(null)

  const sequence = useEditorStore((s) => s.sequence)
  const playhead = useEditorStore((s) => s.playhead)
  const isPlaying = useEditorStore((s) => s.isPlaying)
  const setPlayhead = useEditorStore((s) => s.setPlayhead)
  const setPlaying = useEditorStore((s) => s.setPlaying)
  const setError = useEditorStore((s) => s.setError)
  const duration = useDuration()
  const [volume, setVolume] = useState(1)

  // O engine lê a sequência por callback, não por prop: dentro do loop de desenho ela
  // precisa ser sempre a mais recente, sem depender de um novo render do React.
  const sequenceRef = useRef(sequence)
  sequenceRef.current = sequence

  const assetSources = useMemo(() => {
    const map = new Map<string, MediaSource>()
    for (const asset of assets.values()) {
      // O preview usa o PROXY, nunca o original: ProRes e HEVC 10-bit sequer decodificam
      // no navegador, e um GOP longo torna o scrubbing inutilizável. Imagem não tem
      // proxy — e não precisa.
      const isStill = asset.kind === 'IMAGE'
      const url = isStill ? asset.urls?.original : (asset.urls?.proxy ?? asset.urls?.original)
      if (!url) continue
      map.set(asset.id, {
        assetId: asset.id,
        url,
        hasVideo: asset.hasVideo,
        hasAudio: asset.hasAudio,
        isStill,
      })
    }
    return map
  }, [assets])

  const sourcesRef = useRef(assetSources)
  sourcesRef.current = assetSources

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const engine = new PlaybackEngine(
      canvas,
      () => sequenceRef.current,
      { get: (assetId) => sourcesRef.current.get(assetId) },
      {
        onTimeUpdate: (time) => setPlayhead(time),
        onEnded: () => setPlaying(false),
        onError: (message) => setError(message),
      },
    )
    engineRef.current = engine

    const container = containerRef.current
    const resize = () => {
      const seq = sequenceRef.current
      if (!container || !seq) return
      const box = container.getBoundingClientRect()
      // O canvas ocupa o maior retângulo com a proporção da sequência que cabe no painel.
      const scale = Math.min((box.width - 32) / seq.width, (box.height - 32) / seq.height)
      engine.resize(Math.max(80, seq.width * scale), Math.max(45, seq.height * scale))
    }
    resize()

    const observer = new ResizeObserver(resize)
    if (container) observer.observe(container)

    return () => {
      observer.disconnect()
      engine.dispose()
      engineRef.current = null
    }
  }, [setPlayhead, setPlaying, setError])

  // Uma edição com o player parado precisa aparecer no frame imediatamente.
  useEffect(() => {
    engineRef.current?.refresh()
  }, [sequence])

  // A barra de espaço é o atalho universal de play/pause em editores.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (isTextEntry(event.target)) return

      if (event.code === 'Space') {
        event.preventDefault()
        togglePlay()
      }
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault()
        step(event.key === 'ArrowLeft' ? -1 : 1, event.shiftKey ? 10 : 1)
      }
      if (event.key === 'Home') {
        event.preventDefault()
        seek(0)
      }
      if (event.key === 'End') {
        event.preventDefault()
        seek(duration)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })

  function togglePlay() {
    const engine = engineRef.current
    if (!engine) return
    if (engine.isPlaying) {
      engine.pause()
      setPlaying(false)
    } else {
      void engine.play()
      setPlaying(true)
    }
  }

  function seek(time: number) {
    engineRef.current?.seek(time)
    setPlayhead(time)
  }

  function step(direction: number, frames: number) {
    const fps = sequence?.frameRate ?? { num: 30, den: 1 }
    seek(playhead + direction * framesToSeconds(frames, fps))
  }

  // O playhead pode mudar por fora (clique na timeline); o engine precisa acompanhar.
  useEffect(() => {
    const engine = engineRef.current
    if (!engine || engine.isPlaying) return
    if (Math.abs(engine.time - playhead) > 0.001) engine.seek(playhead)
  }, [playhead])

  const fps = sequence?.frameRate ?? { num: 30, den: 1 }

  return (
    <section className="flex-1 min-w-0 flex flex-col bg-ink-900">
      <div ref={containerRef} className="flex-1 min-h-0 grid place-items-center p-4">
        <canvas
          ref={canvasRef}
          onClick={togglePlay}
          className="rounded shadow-2xl bg-black cursor-pointer max-w-full max-h-full"
        />
      </div>

      <div className="h-11 shrink-0 border-t border-ink-750 bg-ink-850 flex items-center gap-2 px-3">
        <button className="btn-ghost" onClick={() => seek(0)} title="Início (Home)">⏮</button>
        <button className="btn-ghost" onClick={() => step(-1, 1)} title="Frame anterior (←)">◀|</button>
        <button
          className="btn-ghost w-9 justify-center text-base"
          onClick={togglePlay}
          title="Reproduzir / pausar (espaço)"
        >
          {isPlaying ? '❚❚' : '▶'}
        </button>
        <button className="btn-ghost" onClick={() => step(1, 1)} title="Próximo frame (→)">|▶</button>
        <button className="btn-ghost" onClick={() => seek(duration)} title="Fim (End)">⏭</button>

        <div className="ml-2 font-mono text-xs tnum text-ink-200" data-testid="current-time">
          {formatTimecode(playhead, fps)}
          <span className="text-ink-500"> / {formatTimecode(duration, fps)}</span>
        </div>

        <div className="flex-1" />

        <label className="flex items-center gap-2 text-2xs text-ink-400">
          <span aria-hidden>🔊</span>
          <input
            id="preview-volume"
            type="range"
            min={0}
            max={1.5}
            step={0.01}
            value={volume}
            onChange={(e) => {
              const value = Number(e.target.value)
              setVolume(value)
              engineRef.current?.setVolume(value)
            }}
            className="w-20 accent-accent"
            aria-label="Volume do preview"
          />
        </label>
      </div>
    </section>
  )
}
